import { useState, useCallback } from "react";
import { GetOrganizations } from "./GetOrganizations";
import { toast } from "react-toastify";
import { Bundle, Organization } from "fhir/r5";

interface FetchOrganizationsParams {
  orgIds?: string;
  content?: string;
  count?: number;
  applicationAuth?: boolean;
  active?: boolean;
  type?: string;
  name?: string;
}

interface UseOrganizationsReturn {
  organizations: Organization[];
  loading: boolean;
  fetchOrganizations: (
    params: FetchOrganizationsParams
  ) => Promise<Organization[]>;
  clearOrganizations: () => void;
}

/**
 * Hook para gerenciar organizações
 * @returns {UseOrganizationsReturn} Objeto com estados e funções para manipular organizações
 */
export const useOrganizations = (): UseOrganizationsReturn => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOrganizations = useCallback(
    async ({
      orgIds,
      content,
      count,
      applicationAuth,
      active,
      type,
      name,
    }: FetchOrganizationsParams) => {
      setLoading(true);
      try {
        // Buscar organizações com os parâmetros fornecidos
        const response = (await GetOrganizations({
          orgIds,
          content,
          count,
          applicationAuth,
          active,
          type,
          name,
        })) as Bundle;

        if (response?.entry?.length) {
          const organizationsList = response.entry
            .filter(
              (entry): entry is { resource: Organization } =>
                entry.resource?.resourceType === "Organization"
            )
            .map((entry) => entry.resource);

          setOrganizations(organizationsList);
          return organizationsList;
        }

        // Se não houver entries, retorna array vazio
        setOrganizations([]);
        return [];
      } catch (error) {
        toast.error("Erro ao carregar organizações");
        setOrganizations([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearOrganizations = useCallback(() => {
    setOrganizations([]);
  }, []);

  return {
    organizations,
    loading,
    fetchOrganizations,
    clearOrganizations,
  };
};
